import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { QueryKeys } from 'api/queryKeys';
import { userLookup } from 'gen/api';
import type { UserI, UserSearchV2 } from '../../types/types';
import axiosInterceptor from '../interceptor/axiosClient';

export const getUser = async (): Promise<UserI> => {
  const { data } = await axiosInterceptor.get<UserI>('user');

  return data;
};

export type IUserSearchV2Payload = {
  search?: string;
  pageNumber: number;
};

export const userSearchV2 = async ({ search, pageNumber }: IUserSearchV2Payload): Promise<UserSearchV2> => {
  const params = new URLSearchParams({ pageNumber: String(pageNumber), pageSize: '10' });
  if (search) params.set('search', search);

  const { data } = await axiosInterceptor.get<UserSearchV2>(`/user/search-v2?${params.toString()}`);
  return data;
};

/**
 * Names people known only by id, such as those picked in a filter before the page was opened. Ids from another
 * organisation are refused, so a failed lookup leaves them unnamed rather than retrying.
 */
export const useUsersByIds = (ids: string[]) => {
  const sortedIds = ids.toSorted();
  return useQuery({
    queryKey: [QueryKeys.userSearch, 'ids', sortedIds],
    queryFn: async () => {
      const { data } = await userLookup({ body: { userIds: sortedIds }, throwOnError: true });
      return data;
    },
    enabled: sortedIds.length > 0,
    staleTime: 5 * 60_000,
    retry: false,
  });
};

export const useUserSearch = (search: string) =>
  useInfiniteQuery({
    queryKey: [QueryKeys.userSearch, 'pages', search],
    queryFn: ({ pageParam }) => userSearchV2({ search, pageNumber: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      allPages.reduce((sum, page) => sum + page.users.length, 0) < lastPage.total ? allPages.length : undefined,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
